import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, ExternalLink, KeyRound, Loader2, Pencil, ShieldCheck, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  createDeveloperApiKey,
  listDeveloperApiKeyAudit,
  listDeveloperApiKeys,
  revokeDeveloperApiKey,
  updateDeveloperApiKey,
} from "@/lib/developer-access-api";
import {
  ALL_NUMBERS_ACCESS,
  SELECTED_NUMBERS_ACCESS,
  normalizeDeveloperAccessDraft,
  canSaveDeveloperAccessDraft,
} from "@/lib/developer-access";
import { redirectToAuthIfRequired } from "@/lib/auth-required-redirect";
import { useI18n } from "@/lib/i18n";
import { AGENT_COPY } from "@/lib/agent-connect-copy";

function accountLabel(account, tx) {
  return account?.displayName || account?.phoneNumber || tx("Linked number");
}

function scopeGroupCheckedState(draft, group) {
  const scopes = new Set(draft.scopes || []);
  const checkedCount = group.scopes.filter((scope) => scopes.has(scope)).length;
  if (checkedCount === 0) return false;
  return checkedCount === group.scopes.length ? true : "indeterminate";
}

function scopeLabelsForKey(key, scopeGroups) {
  const scopes = new Set(key?.scopes || []);
  return scopeGroups
    .filter((group) => group.scopes.some((scope) => scopes.has(scope)))
    .map((group) => group.label);
}

export default function DeveloperAccessSection({ linkedNumbers = [], activeAccountId = "", tx }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useI18n();
  const agentCopy = AGENT_COPY[locale];
  const [entitlement, setEntitlement] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [keys, setKeys] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [scopeGroups, setScopeGroups] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editingKeyId, setEditingKeyId] = useState("");
  const [revealedKey, setRevealedKey] = useState(null);
  const [auditKeyId, setAuditKeyId] = useState("");
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const auditRequestRef = useRef(0);
  const availableAccounts = accounts.length ? accounts : linkedNumbers;
  const defaultDraft = useMemo(
    () => normalizeDeveloperAccessDraft(
      {
        accountIds: activeAccountId ? [activeAccountId] : [],
      },
      scopeGroups,
      availableAccounts,
      { whatsappAccessAllowed: entitlement?.can_create_keys !== false },
    ),
    [activeAccountId, availableAccounts, scopeGroups, entitlement],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setErrorMessage("");
        const payload = await listDeveloperApiKeys();
        if (cancelled) return;
        setEnabled(Boolean(payload?.featureEnabled));
        setEntitlement(payload?.entitlement || null);
        setKeys(Array.isArray(payload?.keys) ? payload.keys : []);
        setAccounts(Array.isArray(payload?.accounts) ? payload.accounts : []);
        setScopeGroups(Array.isArray(payload?.scopeGroups) ? payload.scopeGroups : []);
      } catch (error) {
        if (cancelled) return;
        if (redirectToAuthIfRequired(error, { navigate, location })) return;
        if (error?.status === 403 || error?.status === 404) {
          setEnabled(false);
          return;
        }
        setErrorMessage(error instanceof Error ? error.message : tx("Could not load API keys."));
        setEnabled(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [location, navigate, tx]);

  function startCreate() {
    setEditingKeyId("");
    setRevealedKey(null);
    setDraft({ ...defaultDraft, name: tx("Agent key") });
  }

  function startEdit(key) {
    setEditingKeyId(key.id);
    setRevealedKey(null);
    setDraft(normalizeDeveloperAccessDraft(key, scopeGroups, availableAccounts, { fallbackSelectedAccount: false }));
  }

  function updateScopeGroup(group, checked) {
    setDraft((current) => {
      const scopes = new Set(current?.scopes || []);
      for (const scope of group.scopes) {
        if (checked) scopes.add(scope);
        else scopes.delete(scope);
      }
      return { ...(current || defaultDraft), scopes: [...scopes] };
    });
  }

  function updateAccount(accountId, checked) {
    setDraft((current) => {
      const accountIds = new Set(current?.accountIds || []);
      if (checked) accountIds.add(accountId);
      else accountIds.delete(accountId);
      return { ...(current || defaultDraft), accountIds: [...accountIds] };
    });
  }

  async function saveDraft() {
    if (!draft || saving) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const payload = editingKeyId
        ? await updateDeveloperApiKey(editingKeyId, draft)
        : await createDeveloperApiKey(draft);
      const savedKey = payload?.key;
      if (savedKey) {
        setKeys((current) => [
          savedKey,
          ...current.filter((key) => key.id !== savedKey.id),
        ]);
      }
      if (payload?.apiKey) {
        setRevealedKey({ key: savedKey, apiKey: payload.apiKey });
      }
      setDraft(null);
      setEditingKeyId("");
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) return;
      setErrorMessage(error instanceof Error ? error.message : tx("Could not save API key."));
    } finally {
      setSaving(false);
    }
  }

  async function revokeKey(key) {
    setSaving(true);
    setErrorMessage("");
    try {
      const payload = await revokeDeveloperApiKey(key.id);
      if (payload?.key) {
        setKeys((current) => current.map((item) => (item.id === key.id ? payload.key : item)));
      }
    } catch (error) {
      if (redirectToAuthIfRequired(error, { navigate, location })) return;
      setErrorMessage(error instanceof Error ? error.message : tx("Could not revoke API key."));
    } finally {
      setSaving(false);
    }
  }

  async function toggleAudit(keyId) {
    const requestId = auditRequestRef.current + 1;
    auditRequestRef.current = requestId;
    if (auditKeyId === keyId) {
      setAuditKeyId("");
      setAuditEvents([]);
      setAuditLoading(false);
      return;
    }
    setAuditKeyId(keyId);
    setAuditEvents([]);
    setAuditLoading(true);
    try {
      const payload = await listDeveloperApiKeyAudit(keyId, { limit: 10 });
      if (auditRequestRef.current !== requestId) return;
      setAuditEvents(Array.isArray(payload?.events) ? payload.events : []);
    } catch (error) {
      if (auditRequestRef.current !== requestId) return;
      setAuditEvents([]);
      if (redirectToAuthIfRequired(error, { navigate, location })) return;
      setErrorMessage(error instanceof Error ? error.message : tx("Could not load audit log."));
    } finally {
      if (auditRequestRef.current === requestId) setAuditLoading(false);
    }
  }

  async function copyRevealedKey() {
    if (!revealedKey?.apiKey || !navigator?.clipboard) return;
    await navigator.clipboard.writeText(revealedKey.apiKey);
  }

  if (enabled !== true) return null;

  return (
    <section className="rounded-3xl border bg-card p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{tx("Developer access")}</p>
          <h2 className="mt-3 font-display text-2xl font-bold text-foreground">{tx("API keys for agents")}</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {tx("Create scoped keys for agents that need to act through your linked WhatsApp numbers.")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button className="h-11 rounded-xl" onClick={startCreate} disabled={loading || saving}>
            <KeyRound className="mr-2 h-4 w-4" />
            {tx("New key")}
          </Button>
          <Button asChild variant="outline" className="h-11 rounded-xl">
            <a href="/api/docs" target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              {tx("API docs")}
            </a>
          </Button>
        </div>
      </div>
      <a href="/agent" className="mt-4 inline-flex text-sm font-medium text-primary">{agentCopy.connect}</a>
      {entitlement?.can_create_keys === false ? <p className="mt-2 text-sm text-muted-foreground">{agentCopy.price}</p> : null}

      {loading ? (
        <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {tx("Loading API keys...")}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="mt-6 rounded-2xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-foreground">
          {tx(errorMessage)}
        </div>
      ) : null}

      {revealedKey?.apiKey ? (
        <div className="ph-no-capture ph-mask mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold">{tx("Copy this key now.")}</p>
              <p className="mt-1 text-emerald-900">{tx("Min will not show it again after you leave this page.")}</p>
            </div>
            <Button type="button" variant="outline" className="h-10 rounded-xl bg-white" onClick={() => void copyRevealedKey()}>
              <Copy className="mr-2 h-4 w-4" />
              {tx("Copy key")}
            </Button>
          </div>
          <code className="ph-no-capture ph-mask mt-3 block overflow-x-auto rounded-xl bg-white px-3 py-2 text-xs text-foreground">
            {revealedKey.apiKey}
          </code>
        </div>
      ) : null}

      {draft ? (
        <div className="mt-6 rounded-2xl border border-border/70 bg-background p-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)]">
            <label className="space-y-2 text-sm font-medium text-foreground">
              <span>{tx("Name")}</span>
              <input
                className="h-11 w-full rounded-xl border bg-background px-3 text-sm"
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...(current || defaultDraft), name: event.target.value }))}
              />
            </label>
            <label className="space-y-2 text-sm font-medium text-foreground">
              <span>{tx("Numbers")}</span>
              <select
                className="h-11 w-full rounded-xl border bg-background px-3 text-sm"
                value={draft.accountAccess}
                onChange={(event) => setDraft((current) => ({
                  ...(current || defaultDraft),
                  accountAccess: event.target.value,
                }))}
              >
                <option value={ALL_NUMBERS_ACCESS}>{tx("All linked numbers")}</option>
                <option value={SELECTED_NUMBERS_ACCESS}>{tx("Selected numbers")}</option>
              </select>
            </label>
          </div>

          {draft.accountAccess === SELECTED_NUMBERS_ACCESS ? (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {availableAccounts.map((account) => (
                <label key={account.id} className="flex items-center justify-between gap-3 rounded-xl border border-border/60 px-3 py-2 text-sm">
                  <span>{accountLabel(account, tx)}</span>
                  <Checkbox
                    checked={(draft.accountIds || []).includes(account.id)}
                    onCheckedChange={(checked) => updateAccount(account.id, checked === true)}
                  />
                </label>
              ))}
            </div>
          ) : null}

          <div className="mt-5">
            <div className="text-sm font-medium text-foreground">{tx("Access")}</div>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {scopeGroups.map((group) => (
                <label key={group.id} className="flex items-start justify-between gap-3 rounded-xl border border-border/60 px-3 py-3 text-sm">
                  <span>
                    <span className="block font-medium text-foreground">{tx(group.label)}</span>
                  </span>
                  <Checkbox
                    checked={scopeGroupCheckedState(draft, group)}
                    onCheckedChange={(checked) => updateScopeGroup(group, checked === true)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => setDraft(null)}>
              {tx("Cancel")}
            </Button>
            <Button type="button" className="h-10 rounded-xl" onClick={() => void saveDraft()} disabled={saving || !canSaveDeveloperAccessDraft(draft, entitlement)}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingKeyId ? tx("Save") : tx("Create key")}
            </Button>
          </div>
        </div>
      ) : null}

      {!loading && !keys.length ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
          {tx("No API keys yet.")}
        </div>
      ) : null}

      {keys.length ? (
        <div className="mt-6 space-y-4">
          {keys.map((key) => (
            <article key={key.id} className="rounded-2xl border border-border/70 bg-background px-4 py-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-foreground">{key.name}</p>
                    <Badge variant="outline" className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]">
                      {tx(key.status === "active" ? "Active" : "Revoked")}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {key.keyPrefix}...{key.keyLast4} · {tx("Last used")} {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : tx("Never")}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {scopeLabelsForKey(key, scopeGroups).map((label) => (
                      <Badge key={label} variant="secondary" className="rounded-full">
                        <ShieldCheck className="mr-1 h-3 w-3" />
                        {tx(label)}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => void toggleAudit(key.id)}>
                    {tx("Audit log")}
                  </Button>
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => startEdit(key)} disabled={key.status !== "active"}>
                    <Pencil className="mr-2 h-4 w-4" />
                    {tx("Edit")}
                  </Button>
                  <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={() => void revokeKey(key)} disabled={key.status !== "active" || saving}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    {tx("Revoke")}
                  </Button>
                </div>
              </div>
              {auditKeyId === key.id ? (
                <div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-3">
                  {auditLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {tx("Loading audit log...")}
                    </div>
                  ) : auditEvents.length ? (
                    <div className="space-y-2">
                      {auditEvents.map((event) => (
                        <div key={event.id} className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-[1fr_auto]">
                          <span>{event.method} {event.path}</span>
                          <span>{tx(event.outcome)} · {event.statusCode}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{tx("No audit events yet.")}</p>
                  )}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
